import { useId, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, post } from "../api";
import "./return-diagnostics.css";

type History = {
  id: string;
  event: string;
  at: string;
  actor: string;
  reason?: string;
  message?: string;
  section: string;
};
type Finding = {
  id: string;
  message: string;
  severity: "blocking" | "advisory";
  status: "open" | "waived";
  canWaive: boolean;
  location: {
    section: string;
    field: string;
    stage: string;
    documentId?: string;
  };
  action: { label: string; stage: string };
  history: History[];
};
type Document = {
  id: string;
  label: string;
  type: string;
  taxYear: number | "";
  canClassify: boolean;
};
export type ReturnCheck = {
  generation: string;
  validUntil: string;
  blockingCount: number;
  advisoryCount: number;
  findings: Finding[];
  documents: Document[];
  history: History[];
  scope: string;
};
const events: Record<string, string> = {
  RAISED: "Issue identified",
  REOPENED: "Issue returned",
  RESOLVED: "Underlying issue corrected",
  WAIVED: "Advisory dismissed",
  STALE: "Information changed; checks need updating",
  EVALUATED: "Checks updated",
};
function HistoryList({ items }: { items: History[] }) {
  return (
    <ul className="rc-history">
      {items.map((e) => (
        <li key={e.id}>
          {events[e.event] || "Review updated"} — {e.actor} ·{" "}
          <time dateTime={e.at}>{new Date(e.at).toLocaleString()}</time>
          {e.message && <p>{e.message}</p>}
          {e.reason && <p>{e.reason}</p>}
        </li>
      ))}
    </ul>
  );
}
function DocumentClassification({
  document,
  pending,
  onSave,
}: {
  document: Document;
  pending: boolean;
  onSave: (
    id: string,
    body: { type: string; taxYear: number; reason: string },
  ) => Promise<void>;
}) {
  const [type, setType] = useState(document.type),
    [year, setYear] = useState(String(document.taxYear)),
    [reason, setReason] = useState(""),
    [error, setError] = useState("");
  return (
    <details className="rc-classification">
      <summary>
        {document.label} — {document.type || "Identify document"}
        {document.taxYear ? ` · ${document.taxYear}` : ""}
      </summary>
      {document.canClassify ? (
        <>
          <label>
            Document type
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">Select document type</option>
              {[
                "W-2",
                "1099-INT",
                "1099-DIV",
                "1099-NEC",
                "1099-R",
                "SSA-1099",
                "OTHER",
              ].map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </label>
          <label>
            Document tax year
            <input
              inputMode="numeric"
              maxLength={4}
              value={year}
              onChange={(e) => setYear(e.target.value)}
            />
          </label>
          <label>
            How did you confirm the type and year?
            <textarea
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          {error && (
            <p role="alert" className="rc-error">
              {error}
            </p>
          )}
          <button
            type="button"
            disabled={pending}
            onClick={async () => {
              if (
                !type ||
                !/^\d{4}$/.test(year) ||
                Number(year) < 1900 ||
                Number(year) > 2200
              ) {
                setError(
                  "Documents — Type and tax year: Select a type and enter a year from 1900 to 2200.",
                );
                return;
              }
              if (reason.trim().length < 10) {
                setError(
                  "Documents — Confirmation explanation: Enter at least 10 characters describing the source you checked.",
                );
                return;
              }
              setError("");
              await onSave(document.id, {
                type,
                taxYear: Number(year),
                reason,
              });
            }}
          >
            Confirm document details
          </button>
        </>
      ) : (
        <p>An assigned preparer or firm owner can correct document details.</p>
      )}
    </details>
  );
}
export function ReturnDiagnostics({
  returnId,
  revision,
  hasUnsavedChanges = false,
  navigateTo,
}: {
  returnId: string;
  revision: number;
  hasUnsavedChanges?: boolean;
  navigateTo: (stage: string, documentId?: string) => void;
}) {
  const heading = useId(),
    cache = useQueryClient(),
    [pending, setPending] = useState(false),
    [error, setError] = useState(""),
    [reasons, setReasons] = useState<Record<string, string>>({});
  const queryKey = ["return-checks", returnId, revision];
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      api<ReturnCheck>(
        `/cases/${encodeURIComponent(returnId)}/diagnostics/evaluate`,
        { method: "POST", body: "{}", signal },
      ),
    enabled: !hasUnsavedChanges && !pending,
    retry: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: "always",
    refetchInterval: 15000,
    placeholderData: undefined,
  });
  const current =
    !hasUnsavedChanges &&
    !pending &&
    !query.isError &&
    !query.isFetching &&
    query.data &&
    Date.parse(query.data.validUntil) > Date.now()
      ? query.data
      : undefined;
  async function mutate(path: string, body: unknown) {
    setError("");
    setPending(true);
    try {
      await post(
        `/cases/${encodeURIComponent(returnId)}/diagnostics/${path}`,
        body,
      );
      setReasons({});
    } catch (e) {
      setError((e as Error).message);
    } finally {
      await cache.invalidateQueries({ queryKey: ["return-checks", returnId] });
      setPending(false);
      await query.refetch();
    }
  }
  return (
    <section className="rb-card return-checks" aria-labelledby={heading}>
      <header className="rc-heading">
        <div>
          <h3 id={heading}>Return checks</h3>
          <p>
            Check saved intake information and supporting documents before
            professional review.
          </p>
        </div>
        <button
          type="button"
          disabled={hasUnsavedChanges || pending || query.isFetching}
          onClick={() => {
            setError("");
            void query.refetch();
          }}
        >
          Update checks
        </button>
      </header>
      {hasUnsavedChanges ? (
        <p role="status">
          Save your changes to update return checks. Earlier findings are not
          current.
        </p>
      ) : query.isError ? (
        <p role="alert" className="rc-error">
          Return checks could not finish: {query.error.message} Review access
          has not been cleared.
        </p>
      ) : !current ? (
        <p role="status">
          Updating return checks… No review clearance is being shown.
        </p>
      ) : (
        <>
          <p role="status">
            <strong>
              {current.blockingCount} blocking{" "}
              {current.blockingCount === 1 ? "issue" : "issues"}
            </strong>{" "}
            · {current.advisoryCount}{" "}
            {current.advisoryCount === 1 ? "advisory" : "advisories"} to review
          </p>
          {current.blockingCount > 0 ? (
            <p>
              Correct the blocking issues before sending the return for review
              or approval. They cannot be dismissed.
            </p>
          ) : (
            <p>
              No blocking intake or document findings remain in the saved
              information checked. This is not tax calculation or filing
              approval.
            </p>
          )}
          <ul className="rc-findings">
            {current.findings.map((f) => (
              <li
                key={f.id}
                className={
                  f.severity === "blocking" ? "rc-blocking" : "rc-advisory"
                }
              >
                <strong>
                  {f.location.section} — {f.location.field}
                </strong>
                <span className="rc-severity">
                  {f.status === "waived"
                    ? "Dismissed by reviewer"
                    : f.severity === "blocking"
                      ? "Must resolve"
                      : "Advisory"}
                </span>
                <p>{f.message}</p>
                <button
                  type="button"
                  onClick={() =>
                    navigateTo(f.action.stage, f.location.documentId)
                  }
                >
                  {f.action.label}
                </button>
                {f.canWaive && (
                  <details>
                    <summary>Record a reviewer decision</summary>
                    <label>
                      Reason for dismissing this advisory
                      <textarea
                        maxLength={2000}
                        value={reasons[f.id] || ""}
                        onChange={(e) =>
                          setReasons({ ...reasons, [f.id]: e.target.value })
                        }
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        if ((reasons[f.id] || "").trim().length < 10) {
                          setError(
                            `${f.location.section} — Reviewer explanation: Enter at least 10 characters explaining why this advisory can be dismissed.`,
                          );
                          return;
                        }
                        void mutate(`${f.id}/waive`, {
                          generation: current.generation,
                          reason: reasons[f.id],
                        });
                      }}
                    >
                      Dismiss advisory with reason
                    </button>
                  </details>
                )}
                <details>
                  <summary>Finding history</summary>
                  <HistoryList items={f.history} />
                </details>
              </li>
            ))}
          </ul>
          {current.documents.length > 0 && (
            <details>
              <summary>Confirm document types and tax years</summary>
              <p>
                Classification identifies the source only. It does not confirm
                extraction or verify an address or identity.
              </p>
              {current.documents.map((d) => (
                <DocumentClassification
                  key={`${current.generation}:${d.id}`}
                  document={d}
                  pending={pending}
                  onSave={(id, body) =>
                    mutate(`documents/${id}/classify`, {
                      generation: current.generation,
                      ...body,
                    })
                  }
                />
              ))}
            </details>
          )}
          <details>
            <summary>Recent check history</summary>
            <p>
              Latest 250 events. Earlier history is retained in the protected
              audit records.
            </p>
            <HistoryList items={current.history} />
          </details>
        </>
      )}
      {error && (
        <p role="alert" className="rc-error">
          {error}
        </p>
      )}
    </section>
  );
}
